import { useState, useRef, useCallback } from 'react';

const projects = [
  {
    location: 'Short Pump, VA',
    windows: '12 windows',
    duration: '2-day install',
    emotional: 'More light. Less noise. Immediate relief.',
    before: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/142f305a-6256-4659-93bc-f04062fc1db5_beforehouse-1.jpeg?v=83ccb2899a39a7300b14185a3fba377e',
    after: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/cdecd9b2-53c6-4a19-b458-a6cab5210a8b_afterhouse-1.jpeg?v=cb0a03f082ee59e3d803cf18694c1949'
  },
  {
    location: 'Glen Allen, VA',
    windows: '8 windows',
    duration: '1-day install',
    emotional: 'From drafty + stuck → calm + comfortable.',
    before: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/c8ad3559-c199-4517-b55a-30e79f823670_beforehouse.jpeg?v=cccf79ecb8d987bdd5369568bb22feb8',
    after: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/d4829f40-194b-4e0c-bb57-b3ea0c5c5c92_afterhouse.jpeg?v=c0e52c51a35361c11a6c9bf9b65fec16'
  },
  {
    location: 'Richmond, VA',
    windows: '15 windows',
    duration: '3-day install',
    emotional: 'Clean lines, brighter rooms, better sleep.',
    before: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/f8bce48e-10a8-4caf-9436-3d01dae4f95d_beforehouse-2.jpeg?v=b8c60b0188edc2157f8c8607071d650e',
    after: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/dd892ad6-08af-4c59-b475-3d14f1fe2173_afterwindow.jpeg?v=662d4d1da3b30124609b64a94e8862dd'
  },
  {
    location: 'Henrico, VA',
    windows: '10 windows',
    duration: '2-day install',
    emotional: 'Brighter mornings. Less draft. Instant relief.',
    before: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/aa03ab3f-c54c-4681-8258-ca28e23ee9c2_beforehouse4.jpg?v=96efa1e19c1cde7f302546b282d8742a',
    after: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/f625120c-cbba-4cb1-a2c0-83300235482d_afterhouse4.jpg?v=a6881f3344d2aa2d02716e71b29eb41b'
  }
];

const afterOnlyPhotos = [
  { image: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/cdecd9b2-53c6-4a19-b458-a6cab5210a8b_afterhouse-1.jpeg?v=cb0a03f082ee59e3d803cf18694c1949', location: 'Midlothian, VA' },
  { image: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/d4829f40-194b-4e0c-bb57-b3ea0c5c5c92_afterhouse.jpeg?v=c0e52c51a35361c11a6c9bf9b65fec16', location: 'Chesterfield, VA' },
  { image: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/f625120c-cbba-4cb1-a2c0-83300235482d_afterhouse4.jpg?v=a6881f3344d2aa2d02716e71b29eb41b', location: 'Glen Allen, VA' },
  { image: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/dd65516b-604a-499a-b949-96d8d8328413_afterhouse6.jpg?v=167791c4cfdaf9549130a3550f111839', location: 'Bon Air, VA' },
  { image: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/dd892ad6-08af-4c59-b475-3d14f1fe2173_afterwindow.jpeg?v=662d4d1da3b30124609b64a94e8862dd', location: 'Mechanicsville, VA' },
  { image: 'https://storage.readdy-site.link/project_files/0a6352e6-3e3a-436a-bb14-6a736f540368/d7e2dfb0-3926-42e8-9758-320b0fcd218b_beforehouse2.jpg?v=dbeabf93f70c1a29c87b57c652b9cace', location: 'Lakeside, VA' },
];

interface BeforeAfterSliderProps {
  project: typeof projects[0];
}

function BeforeAfterSlider({ project }: BeforeAfterSliderProps) {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const getPercent = useCallback((clientX: number) => {
    if (!containerRef.current) return 50;
    const rect = containerRef.current.getBoundingClientRect();
    const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
    return Math.max(5, Math.min((x / rect.width) * 100, 95));
  }, []);

  const handleMouseDown = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!isDragging) return;
    setSliderPosition(getPercent(e.clientX));
  };

  const handleMouseUp = () => setIsDragging(false);

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setSliderPosition(getPercent(e.touches[0].clientX));
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging) return;
    e.preventDefault();
    setSliderPosition(getPercent(e.touches[0].clientX));
  };

  const handleTouchEnd = () => setIsDragging(false);

  return (
    <div
      ref={containerRef}
      className="relative w-full h-[260px] sm:h-[340px] md:h-[400px] rounded-2xl overflow-hidden select-none shadow-lg touch-none"
      style={{ cursor: isDragging ? 'ew-resize' : 'col-resize' }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onMouseLeave={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      {/* After Image (base) */}
      <div className="absolute inset-0">
        <img src={project.after} alt={`${project.location} after`} className="w-full h-full object-cover object-top" />
        <div className="absolute top-3 right-3 px-2.5 py-1 bg-[#52AB98] text-white text-[11px] font-bold rounded-full tracking-wide shadow">
          AFTER
        </div>
      </div>

      {/* Before Image (clipped) */}
      <div
        className="absolute inset-0 overflow-hidden"
        style={{ clipPath: `inset(0 ${100 - sliderPosition}% 0 0)` }}
      >
        <img src={project.before} alt={`${project.location} before`} className="w-full h-full object-cover object-top" />
        <div className="absolute top-3 left-3 px-2.5 py-1 bg-[#2B6777] text-white text-[11px] font-bold rounded-full tracking-wide shadow">
          BEFORE
        </div>
      </div>

      {/* Divider line */}
      <div
        className="absolute top-0 bottom-0 w-[2px] bg-white shadow-md pointer-events-none"
        style={{ left: `${sliderPosition}%` }}
      />

      {/* Drag Handle */}
      <div
        className="absolute top-1/2 -translate-y-1/2 -translate-x-1/2 w-11 h-11 bg-white rounded-full shadow-xl flex items-center justify-center pointer-events-none border-2 border-[#52AB98]/30"
        style={{ left: `${sliderPosition}%` }}
      >
        <div className="flex items-center gap-0.5">
          <i className="ri-arrow-left-s-line text-[#2B6777] text-base leading-none"></i>
          <i className="ri-arrow-right-s-line text-[#2B6777] text-base leading-none"></i>
        </div>
      </div>

      {/* Hint overlay (fades after first interaction) */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 pointer-events-none">
        <span className="text-[10px] text-white/80 bg-black/30 px-2 py-0.5 rounded-full backdrop-blur-sm">
          Drag to compare
        </span>
      </div>
    </div>
  );
}

export default function OurWork() {
  const [activeProject, setActiveProject] = useState(0);

  const scrollToContact = () => {
    const element = document.getElementById('contact');
    if (element) {
      const offset = 80;
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  return (
    <section id="our-work" className="py-section bg-white">
      <div className="max-w-content mx-auto px-4 sm:px-6">

        {/* Header */}
        <div className="text-center mb-8 sm:mb-10">
          {/* SNAP Windows Logo */}
          <div className="flex justify-center mb-5">
            <img
              src="https://static.readdy.ai/image/a109cef237b31571116f6f567dd6e069/da3f45283dde3421a52ea41a6bce2f15.png"
              alt="SNAP Windows Logo"
              className="h-12 sm:h-14 w-auto object-contain"
            />
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-heading font-semibold text-[#2B6777] mb-3 tracking-tight">
            Real homes. Real results.
          </h2>
          <p className="text-base sm:text-lg text-[#2B6777]/80 mb-1 font-medium">
            See what happens when clarity, quality, and craftsmanship come together.
          </p>
          <p className="text-sm text-[#2B6777]/50">
            Real projects in Virginia. No stock photos.
          </p>
        </div>

        {/* Mobile: Tab selector for projects */}
        <div className="flex gap-2 overflow-x-auto pb-2 mb-5 scrollbar-hide snap-x snap-mandatory md:hidden">
          {projects.map((p, i) => (
            <button
              key={i}
              onClick={() => setActiveProject(i)}
              className={`flex-shrink-0 snap-start px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all cursor-pointer border ${
                activeProject === i
                  ? 'bg-[#2B6777] text-white border-[#2B6777] shadow-md'
                  : 'bg-white text-[#2B6777] border-[#2B6777]/30'
              }`}
            >
              {p.location}
            </button>
          ))}
        </div>

        {/* Mobile: Single active slider */}
        <div className="md:hidden mb-4">
          <BeforeAfterSlider project={projects[activeProject]} />
          <div className="mt-3 px-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 mb-1">
              <h3 className="text-base font-heading font-semibold text-[#2B6777]">
                {projects[activeProject].location}
              </h3>
              <span className="text-xs text-[#2B6777]/40">•</span>
              <span className="text-xs text-[#2B6777]/60">{projects[activeProject].windows}</span>
              <span className="text-xs text-[#2B6777]/40">•</span>
              <span className="text-xs text-[#2B6777]/60">{projects[activeProject].duration}</span>
            </div>
            <p className="text-sm text-[#52AB98] font-medium italic">
              {projects[activeProject].emotional}
            </p>
          </div>

          {/* Dot indicators */}
          <div className="flex justify-center gap-2 mt-4">
            {projects.map((_, i) => (
              <button
                key={i}
                onClick={() => setActiveProject(i)}
                className={`w-2 h-2 rounded-full transition-all cursor-pointer ${
                  activeProject === i ? 'bg-[#52AB98] w-5' : 'bg-[#2B6777]/20'
                }`}
              />
            ))}
          </div>
        </div>

        {/* Desktop: 2-column grid */}
        <div className="hidden md:grid grid-cols-2 gap-8 mb-16">
          {projects.map((project, index) => (
            <div key={index}>
              <BeforeAfterSlider project={project} />
              <div className="mt-4">
                <div className="flex items-center gap-3 mb-1.5">
                  <h3 className="text-lg font-heading font-semibold text-[#2B6777]">
                    {project.location}
                  </h3>
                  <span className="text-sm text-[#2B6777]/40">•</span>
                  <span className="text-sm text-[#2B6777]/60">{project.windows}</span>
                  <span className="text-sm text-[#2B6777]/40">•</span>
                  <span className="text-sm text-[#2B6777]/60">{project.duration}</span>
                </div>
                <p className="text-base text-[#52AB98] font-medium italic">
                  {project.emotional}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* More Transformations */}
        <div className="mb-10 mt-10 md:mt-0">
          <h3 className="text-xl sm:text-2xl font-heading font-semibold text-[#2B6777] mb-5 text-center">
            More Transformations
          </h3>

          {/* Mobile: horizontal scroll */}
          <div className="flex gap-3 overflow-x-auto pb-3 scrollbar-hide snap-x snap-mandatory md:hidden">
            {afterOnlyPhotos.map((photo, index) => (
              <div
                key={index}
                className="flex-shrink-0 snap-start w-[72vw] max-w-[280px] relative overflow-hidden rounded-xl shadow-md"
              >
                <img
                  src={photo.image}
                  alt={photo.location}
                  className="w-full h-[180px] object-cover object-top"
                />
                <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/60 to-transparent p-3">
                  <p className="text-xs font-medium text-white">{photo.location}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Desktop: grid */}
          <div className="hidden md:grid grid-cols-3 gap-4">
            {afterOnlyPhotos.map((photo, index) => (
              <div key={index} className="group relative overflow-hidden rounded-xl shadow-md hover:shadow-xl transition-shadow duration-300">
                <img
                  src={photo.image}
                  alt={photo.location}
                  className="w-full h-[220px] object-cover object-top group-hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                <div className="absolute bottom-0 left-0 right-0 p-4 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-sm font-medium text-white">{photo.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="text-center pt-2">
          <a
            href="https://api.leadconnectorhq.com/widget/form/0ZsIY0jYLaF8aXm2FeXI"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-7 py-4 bg-[#52AB98] text-white text-base sm:text-lg font-semibold rounded-xl hover:bg-[#478F82] transition-colors cursor-pointer whitespace-nowrap shadow-lg"
          >
            Get My Free Quote
            <i className="ri-arrow-right-line text-lg"></i>
          </a>
          <p className="text-xs text-[#2B6777]/50 mt-3">No in-home visit required. No money down.</p>
        </div>

      </div>
    </section>
  );
}
